import _Typography from "@mui/material/Typography";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUnits } from './units';
export function useChallenge() {
  const {
    t
  } = useTranslation();
  const {
    formatNumber,
    toDistance,
    toTime
  } = useUnits();
  function getProgressValue(type, progressValue, {
    withoutPointsUnit
  } = {}) {
    switch (type) {
      case 'traveledDistance':
        return toDistance(progressValue);
      case 'cyclingDays':
        return t('commons.day', {
          count: Math.round(progressValue * 10) / 10
        });
      case 'homeWorkJourneysCount':
      case 'journeysCount':
        return t('commons.journeys', {
          count: progressValue,
          countFormatted: formatNumber(progressValue)
        });
      case 'bttf':
      case 'custom':
        {
          const rounded = Math.round(progressValue * 10) / 10;
          return withoutPointsUnit ? formatNumber(rounded) : t('commons.stats.points', {
            count: rounded,
            countFormatted: formatNumber(rounded)
          });
        }
      default:
        return withoutPointsUnit ? `${Math.round(progressValue * 10) / 10}` : `${Math.round(progressValue * 10) / 10} pts`;
    }
  }
  function getProgressValueWithUnit(type, progressValue, {
    withoutPointsUnit
  } = {}) {
    switch (type) {
      case 'traveledDistance':
        return t('commons.stats.distance', {
          count: Math.floor(progressValue / 1000),
          distance: toDistance(progressValue)
        });
      case 'cyclingDays':
        return t('commons.stats.active_days', {
          count: progressValue
        });
      case 'homeWorkJourneysCount':
      case 'journeysCount':
        return t('commons.stats.journeys', {
          count: progressValue,
          countFormatted: formatNumber(progressValue)
        });
      case 'bttf':
      case 'custom':
        {
          const rounded = Math.round(progressValue * 10) / 10;
          return withoutPointsUnit ? formatNumber(rounded) : t('commons.stats.points', {
            count: rounded,
            countFormatted: formatNumber(rounded)
          });
        }
      default:
        return withoutPointsUnit ? `${Math.round(progressValue * 10) / 10}` : `${Math.round(progressValue * 10) / 10} pts`;
    }
  }

  /** Formats a leaderboard `progressValue` according to the selected `rank_field` — only
   * meaningful for `custom` challenges, where the ranking metric can be switched away from the
   * default `progress_value` (points). */
  function getRankFieldValueWithUnit(rankField, progressValue) {
    switch (rankField) {
      case 'sum_trace_distance_m':
      case 'sum_commuting_trace_distance_m':
        return toDistance(progressValue);
      case 'count_cycling_days':
      case 'count_commuting_days':
        return t('commons.day', {
          count: progressValue
        });
      case 'count_trace':
      case 'count_commuting_trace':
        return t('commons.journeys', {
          count: progressValue,
          countFormatted: formatNumber(progressValue)
        });
      case 'sum_trace_duration_s':
        return toTime(progressValue);
      case 'sum_trace_positive_denivele_m':
        return `${formatNumber(Math.round(progressValue))} m`;
      case 'progress_value':
      default:
        {
          const rounded = Math.round(progressValue * 10) / 10;
          return t('commons.stats.points', {
            count: rounded,
            countFormatted: formatNumber(rounded)
          });
        }
    }
  }
  function getFormattedProgressValue(challenge, {
    highlight
  } = {}) {
    const {
      type,
      period,
      targetValue,
      progressValue
    } = challenge;
    if (period === 'future' && !targetValue) return '-';
    switch (type) {
      case 'traveledDistance':
        return period === 'future' ? t('commons.challenge_details.objective', {
          target: toDistance(targetValue)
        }) : highlight ? targetValue ? /*#__PURE__*/React.createElement(React.Fragment, null, t('commons.challenge_details.objective_without_target'), "\xA0", /*#__PURE__*/React.createElement(_Typography, {
          color: "#048760",
          component: "span",
          fontSize: "inherit",
          fontWeight: 700
        }, toDistance(progressValue)), "\xA0/\xA0", toDistance(targetValue)) : /*#__PURE__*/React.createElement(_Typography, {
          color: "#048760",
          component: "span",
          fontSize: "inherit",
          fontWeight: 700
        }, toDistance(progressValue)) : /*#__PURE__*/React.createElement(React.Fragment, null, toDistance(progressValue), targetValue ? ` / ${toDistance(targetValue)}` : '');
      case 'cyclingDays':
        return period === 'future' ? t('commons.challenge_details.objective', {
          target: t('commons.day', {
            count: targetValue
          })
        }) : highlight ? /*#__PURE__*/React.createElement(React.Fragment, null, targetValue ? /*#__PURE__*/React.createElement(React.Fragment, null, t('commons.challenge_details.objective_without_target'), "\xA0", /*#__PURE__*/React.createElement(_Typography, {
          color: "#048760",
          component: "span",
          fontSize: "inherit",
          fontWeight: 700
        }, progressValue), "\xA0/\xA0", t('commons.day', {
          count: targetValue
        })) : /*#__PURE__*/React.createElement(_Typography, {
          color: "#048760",
          component: "span",
          fontSize: "inherit",
          fontWeight: 700
        }, t('commons.day', {
          count: progressValue
        }))) : /*#__PURE__*/React.createElement(React.Fragment, null, targetValue ? `${progressValue} / ${t('commons.day', {
          count: targetValue
        })}` : t('commons.day', {
          count: progressValue
        }));
      case 'homeWorkJourneysCount':
      case 'journeysCount':
        return period === 'future' ? t('commons.challenge_details.objective', {
          target: t('commons.journeys', {
            count: targetValue,
            countFormatted: formatNumber(targetValue)
          })
        }) : highlight ? /*#__PURE__*/React.createElement(React.Fragment, null, targetValue ? /*#__PURE__*/React.createElement(React.Fragment, null, t('commons.challenge_details.objective_without_target'), "\xA0", /*#__PURE__*/React.createElement(_Typography, {
          color: "#048760",
          component: "span",
          fontSize: "inherit",
          fontWeight: 700
        }, progressValue), "\xA0/\xA0", t('commons.journeys', {
          count: targetValue,
          countFormatted: formatNumber(targetValue)
        })) : /*#__PURE__*/React.createElement(_Typography, {
          color: "#048760",
          component: "span",
          fontSize: "inherit",
          fontWeight: 700
        }, t('commons.journeys', {
          count: progressValue,
          countFormatted: formatNumber(progressValue)
        }))) : /*#__PURE__*/React.createElement(React.Fragment, null, targetValue ? `${progressValue} / ${t('commons.journeys', {
          count: targetValue,
          countFormatted: formatNumber(targetValue)
        })}` : t('commons.journeys', {
          count: progressValue,
          countFormatted: formatNumber(progressValue)
        }));
      case 'bttf':
      case 'custom':
        {
          const formattedProgressValue = formatNumber(progressValue.toFixed(1));
          return period === 'future' ? targetValue ? t('commons.challenge_details.objective', {
            target: t('commons.stats.points', {
              count: targetValue
            })
          }) : t('commons.stats.points', {
            count: targetValue
          }) : highlight ? /*#__PURE__*/React.createElement(React.Fragment, null, targetValue ? /*#__PURE__*/React.createElement(React.Fragment, null, t('commons.challenge_details.objective_without_target'), "\xA0", /*#__PURE__*/React.createElement(_Typography, {
            color: "#048760",
            component: "span",
            fontSize: "inherit",
            fontWeight: 700
          }, formattedProgressValue), "\xA0/\xA0", t('commons.stats.points', {
            count: targetValue
          })) : /*#__PURE__*/React.createElement(_Typography, {
            color: "#048760",
            component: "span",
            fontSize: "inherit",
            fontWeight: 700
          }, t('commons.stats.points_precise', {
            count: progressValue,
            countFormatted: formattedProgressValue
          }))) : /*#__PURE__*/React.createElement(React.Fragment, null, targetValue ? `${formattedProgressValue} / ${t('commons.stats.points', {
            count: targetValue
          })}` : t('commons.stats.points_precise', {
            count: progressValue,
            countFormatted: formattedProgressValue
          }));
        }
      default:
        return;
    }
  }
  return {
    getProgressValue,
    getProgressValueWithUnit,
    getRankFieldValueWithUnit,
    getFormattedProgressValue
  };
}